'use client'

import * as React from 'react'
import NextImage from 'next/image'
import { AssetGallery } from './asset-gallery'
import { useRouter } from 'next/dist/client/components/navigation'

type RawAsset =
  | {
      id?: string | number | null
      url?: string | null
      optimizedUrl?: string | null
      width?: number | null
      height?: number | null
    }
  | null
  | undefined

interface Props {
  assets?: RawAsset[] | null
  commentId: string
  hideInline?: boolean
  photoHref?: string | null
  prevQuery?: string
  onOpenPhoto?: (e: React.MouseEvent) => void
  className?: string
  imageMaxWidthPx?: number
  imageHeightPx?: number
  imageSizes?: string
  imageClassName?: string
  disableClick?: boolean
}

export function DiscussionCommentMedia({
  assets,
  commentId,
  hideInline,
  photoHref,
  prevQuery,
  onOpenPhoto,
  className = 'mt-3',
  imageMaxWidthPx = 300,
  imageHeightPx = 200,
  imageSizes = '(max-width: 480px) 100vw, 300px',
  imageClassName = 'relative my-2 rounded-2 object-cover max-w-full h-auto tablet:max-h-[210px]',
  disableClick,
}: Props) {
  const router = useRouter()

  const galleryImages = React.useMemo(
    () =>
      (assets ?? [])
        .map(a => ({
          id: String(a?.id ?? ''),
          url: a?.optimizedUrl || a?.url || '',
        }))
        .filter(x => x.id && x.url),
    [assets],
  )

  const hasMultipleImages = galleryImages.length > 1
  const firstAssetUrl = galleryImages[0]?.url

  const handleClick = (e: React.MouseEvent) => {
    onOpenPhoto?.(e)
  }

  if (hideInline) {
    return null
  }
  if (!galleryImages.length) {
    return null
  }

  if (hasMultipleImages) {
    return (
      <div className={className}>
        <AssetGallery
          assetUrls={galleryImages}
          commentId={commentId}
          prevQuery={prevQuery ?? ''}
          key={String(commentId)}
        />
      </div>
    )
  }

  if (disableClick) {
    return (
      <NextImage
        src={firstAssetUrl}
        alt="Comment image"
        width={imageMaxWidthPx}
        height={imageHeightPx}
        sizes={imageSizes}
        className={imageClassName}
        style={{ width: `clamp(0px, 100%, ${imageMaxWidthPx}px)` }}
        loading="lazy"
      />
    )
  }

  return (
    <div className={className}>
      {photoHref && firstAssetUrl ? (
        <a
          href={photoHref}
          onMouseEnter={() => {
            // Prefetch client transition
            try {
              router.prefetch(photoHref)
            } catch {}
            // Warm the image (careful: avoid shadowing next/image's default export)
            const u = firstAssetUrl
            if (u) {
              const img = new window.Image()
              img.decoding = 'async'
              img.loading = 'eager'
              img.src = u
            }
          }}
          onClick={e => handleClick(e)}
          className="inline-block"
          aria-label="Open image in viewer"
        >
          <NextImage
            src={firstAssetUrl}
            alt="Comment image"
            width={imageMaxWidthPx}
            height={imageHeightPx}
            sizes={imageSizes}
            className={imageClassName}
            style={{ width: `clamp(0px, 100%, ${imageMaxWidthPx}px)` }}
            loading="lazy"
          />
        </a>
      ) : null}
    </div>
  )
}
